import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Swatches, UploadSimple, Trash, Stamp, ImageSquare, Info } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, API, formatApiErrorDetail } from "@/lib/api";

const readFile = (f) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(f);
});

function Card({ title, subtitle, children, testId }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid={testId}>
      <h3 className="font-heading text-sm font-extrabold text-[#0A0D14]">{title}</h3>
      {subtitle && <p className="text-[11px] text-[#8a90a0] mt-0.5 mb-4">{subtitle}</p>}
      {children}
    </div>
  );
}

export default function Brand() {
  const [brand, setBrand] = useState(null);
  const [refs, setRefs] = useState([]);
  const [colorsInput, setColorsInput] = useState("");
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);
  const refFileRef = useRef(null);

  const loadRefs = () => api.get("/team/brand/refs").then(({ data }) => setRefs(data)).catch(() => {});

  useEffect(() => {
    api.get("/team/brand").then(({ data }) => { setBrand(data); setColorsInput((data.colors || []).join(", ")); }).catch(() => {});
    loadRefs();
  }, []);

  const put = async (payload, msg) => {
    try {
      const { data } = await api.put("/team/brand", payload);
      setBrand(data);
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Logo must be under 4MB"); return; }
    const dataUrl = await readFile(f);
    await put({ logo_b64: dataUrl }, "Logo saved — stamped pixel-perfect on every creative, the AI never touches it");
  };

  const saveAll = async () => {
    setSaving(true);
    await put({
      colors: colorsInput.split(",").map((c) => c.trim()).filter(Boolean),
      brand_name: brand.brand_name || "", tagline: brand.tagline || "",
      website: brand.website || "", guidelines: brand.guidelines || "",
      default_style: brand.default_style, default_corner: brand.default_corner,
    }, "Brand Kit saved — the AI team designs with it from the next creative");
    setSaving(false);
  };

  const uploadRef = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files) {
      if (f.size > 4 * 1024 * 1024) { toast.error(`${f.name} is over 4MB`); continue; }
      try {
        const dataUrl = await readFile(f);
        await api.post("/team/brand/refs", { image_b64: dataUrl, name: f.name });
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
        break;
      }
    }
    loadRefs();
  };

  const deleteRef = async (rid) => {
    await api.delete(`/team/brand/refs/${rid}`).catch(() => {});
    loadRefs();
  };

  if (!brand) return null;
  const swatches = colorsInput.split(",").map((c) => c.trim()).filter(Boolean);

  return (
    <div className="space-y-5 max-w-4xl" data-testid="brand-page">
      <div className="ai-panel rounded-lg p-6 flex items-center gap-4">
        <span className="h-10 w-10 rounded-lg bg-ai/10 flex items-center justify-center"><Swatches size={20} weight="duotone" className="text-ai" /></span>
        <div>
          <h2 className="font-heading text-base font-extrabold tracking-tight text-[#0A0D14]">Brand Kit</h2>
          <p className="text-[11px] text-[#8a90a0]">Everything the AI Creative Team must respect — your exact logo is stamped programmatically, never drawn or edited by AI</p>
        </div>
      </div>

      <Card title="Logo & Default Placement" subtitle="Stamped bit-for-bit onto every creative after the AI paints the image. Re-stamp any creative with a different corner via its 'Logo' button." testId="brand-logo-card">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Your logo</span>
            <div className="flex items-center gap-3">
              {brand.has_logo && (
                <img src={`${API}/team/brand/logo?v=${encodeURIComponent(brand.updated_at || "")}`} alt="brand logo"
                  className="h-14 rounded-lg border border-[#E5E7EB] bg-white p-1.5" data-testid="brand-logo-preview" />
              )}
              <input ref={logoRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={uploadLogo} data-testid="brand-logo-input" />
              <Button variant="outline" onClick={() => logoRef.current?.click()} data-testid="brand-logo-upload"
                className="rounded-full border-[#E5E7EB] font-bold text-xs h-10 px-4">
                <UploadSimple size={14} className="mr-1.5" /> {brand.has_logo ? "Replace logo" : "Upload logo PNG"}
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Default style</span>
            <Select value={brand.default_style} onValueChange={(v) => setBrand({ ...brand, default_style: v })}>
              <SelectTrigger className="w-36 rounded-lg bg-white h-10 text-xs" data-testid="brand-default-style"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="badge">White badge</SelectItem>
                <SelectItem value="direct">Direct</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Default corner</span>
            <Select value={brand.default_corner} onValueChange={(v) => setBrand({ ...brand, default_corner: v })}>
              <SelectTrigger className="w-36 rounded-lg bg-white h-10 text-xs" data-testid="brand-default-corner"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="br">Bottom right</SelectItem>
                <SelectItem value="bl">Bottom left</SelectItem>
                <SelectItem value="tr">Top right</SelectItem>
                <SelectItem value="tl">Top left</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card title="Identity & Colors" subtitle="Riya designs with your palette; Arjun writes copy in your name and tone." testId="brand-identity-card">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Brand name</span>
            <Input value={brand.brand_name} onChange={(e) => setBrand({ ...brand, brand_name: e.target.value })}
              placeholder="Training Basket" data-testid="brand-name-input" className="h-10 rounded-lg" />
          </div>
          <div className="space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Tagline</span>
            <Input value={brand.tagline} onChange={(e) => setBrand({ ...brand, tagline: e.target.value })}
              placeholder="Qualify for Tomorrow" data-testid="brand-tagline-input" className="h-10 rounded-lg" />
          </div>
          <div className="space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Website</span>
            <Input value={brand.website} onChange={(e) => setBrand({ ...brand, website: e.target.value })}
              placeholder="https://trainingbasket.in" data-testid="brand-website-input" className="h-10 rounded-lg" />
          </div>
          <div className="space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Brand colors (comma-separated hex)</span>
            <div className="flex items-center gap-2">
              <Input value={colorsInput} onChange={(e) => setColorsInput(e.target.value)}
                placeholder="#FFF200, #1A1A1A" data-testid="brand-colors-input" className="h-10 rounded-lg font-mono text-xs flex-1" />
              <div className="flex gap-1">
                {swatches.map((c, i) => <span key={i} className="h-6 w-6 rounded-md border border-[#E5E7EB]" style={{ background: c }} />)}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card title="Voice & Guidelines" subtitle="Do's and don'ts every teammate follows — tone, claims to avoid, must-mention points, target audience notes." testId="brand-voice-card">
        <Textarea value={brand.guidelines} onChange={(e) => setBrand({ ...brand, guidelines: e.target.value })}
          rows={4} data-testid="brand-guidelines-input" className="rounded-lg text-sm"
          placeholder={"e.g. Always speak to working professionals in Delhi-NCR. Confident but never salesy. Never promise guaranteed jobs — say 'placement assistance'. Mention 100% practical training."} />
      </Card>

      <Card title="Reference Library" subtitle="Upload up to 6 sample ads (competitors or past winners). Riya automatically uses the 3 newest as style inspiration for every new creative — logos and brand names are never copied." testId="brand-refs-card">
        <div className="flex flex-wrap gap-3" data-testid="brand-ref-grid">
          {refs.map((r) => (
            <div key={r.id} className="relative group">
              <img src={`${API}/team/brand/refs/${r.id}/image`} alt={r.name} className="h-28 w-28 rounded-lg object-cover border border-[#E5E7EB]" />
              <button onClick={() => deleteRef(r.id)} data-testid={`brand-ref-delete-${r.id}`}
                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-[#0A0D14] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash size={10} weight="bold" />
              </button>
            </div>
          ))}
          <input ref={refFileRef} type="file" accept="image/*" multiple className="hidden" onChange={uploadRef} data-testid="brand-ref-input" />
          {refs.length < 6 && (
            <button onClick={() => refFileRef.current?.click()} data-testid="brand-ref-upload"
              className="h-28 w-28 rounded-lg border-2 border-dashed border-[#c9cdd6] flex flex-col items-center justify-center gap-1 text-[#8a90a0] hover:border-ai hover:text-ai transition-colors">
              <ImageSquare size={20} weight="duotone" />
              <span className="text-[10px] font-bold">Add sample</span>
            </button>
          )}
        </div>
      </Card>

      <div className="flex items-center gap-3">
        <Button onClick={saveAll} disabled={saving} data-testid="brand-save-button"
          className="rounded-full bg-[#0A0D14] hover:bg-[#1c2230] text-white font-bold h-11 px-6">
          <Stamp size={15} className="mr-1.5" /> {saving ? "Saving…" : "Save Brand Kit"}
        </Button>
        <span className="flex items-center gap-1.5 text-[11px] text-[#8a90a0]"><Info size={13} /> Logo and samples save instantly on upload; identity, colors and guidelines save with this button.</span>
      </div>
    </div>
  );
}
